import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { motion } from 'framer-motion';
import './StripeCheckout.css';

interface StripeCheckoutProps {
  amount: number;
  onSuccess: (paymentMethodId: string) => void;
  onCancel: () => void;
}

const StripeCheckout: React.FC<StripeCheckoutProps> = ({ amount, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Créer un PaymentMethod
      if (!cardElement) {
        setError('Élément de carte non disponible');
        setProcessing(false);
        return;
      }

      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message || 'Erreur de paiement');
        setProcessing(false);
        return;
      }

      // Simuler le paiement (en production, vous devez appeler votre backend)
      // Pour le moment, on considère le paiement comme réussi
      setTimeout(() => {
        setProcessing(false);
        onSuccess(paymentMethod.id);
      }, 1500);

    } catch (err) {
      setError('Une erreur est survenue lors du paiement');
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#2C3E50',
        fontFamily: 'Poppins, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
        iconColor: '#FF6B35',
      },
      invalid: {
        color: '#E74C3C',
        iconColor: '#E74C3C',
      },
    },
    hidePostalCode: true,
  };

  return (
    <div className="stripe-checkout">
      <h3>💳 Paiement Sécurisé</h3>
      <p className="amount-display">Montant à payer : <strong>{amount.toFixed(2)}€</strong></p>

      <form onSubmit={handleSubmit} className="stripe-form">
        <div className="card-element-container">
          <CardElement options={cardElementOptions} />
        </div>

        {error && (
          <motion.div
            className="error-message"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.div>
        )}

        <div className="stripe-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={processing}
          >
            Annuler
          </button>
          <motion.button
            type="submit"
            className="btn-pay"
            disabled={!stripe || processing}
            whileHover={{ scale: processing ? 1 : 1.02 }}
            whileTap={{ scale: processing ? 1 : 0.98 }}
          >
            {processing ? 'Traitement...' : `Payer ${amount.toFixed(2)}€`}
          </motion.button>
        </div>
      </form>

      <div className="secure-badge">
        <span>🔒</span>
        <p>Paiement sécurisé par Stripe</p>
      </div>
    </div>
  );
};

export default StripeCheckout;
